"use client";

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import { Search, AlertTriangle, Star } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SUBSPECIALTIES } from "@/lib/constants";

interface SearchEntry {
  slug: string;
  subspecialty: string;
  title: string;
  titleEn: string;
  category: string;
  aliases: string[];
  keywords: string[];
  difficulty: string | null;
  emergency: boolean;
  examImportance: string | null;
}

interface SearchCommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Normalize a string for search: lowercase, trim, collapse whitespace.
 * Works for both Korean (한국어) and English.
 */
function normalize(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * Score a search entry against a query. Returns 0 for no match, higher for better matches.
 */
function scoreEntry(entry: SearchEntry, query: string): number {
  const q = normalize(query);
  if (!q) return 0;

  const titleNorm = normalize(entry.title);
  const titleEnNorm = normalize(entry.titleEn);

  // Exact title match (Korean)
  if (titleNorm === q) return 100;
  // Exact title match (English)
  if (titleEnNorm === q) return 95;

  // Title starts with query
  if (titleNorm.startsWith(q)) return 90;
  if (titleEnNorm.startsWith(q)) return 85;

  // Title contains query
  if (titleNorm.includes(q)) return 80;
  if (titleEnNorm.includes(q)) return 75;

  // Alias exact match
  for (const alias of entry.aliases) {
    const aliasNorm = normalize(alias);
    if (aliasNorm === q) return 70;
    if (aliasNorm.startsWith(q)) return 65;
    if (aliasNorm.includes(q)) return 60;
  }

  // Keyword match
  for (const kw of entry.keywords) {
    const kwNorm = normalize(kw);
    if (kwNorm === q) return 55;
    if (kwNorm.startsWith(q)) return 50;
    if (kwNorm.includes(q)) return 45;
  }

  // Category match
  const catNorm = normalize(entry.category);
  if (catNorm.includes(q)) return 30;

  // Slug match (useful for English partial searches)
  if (entry.slug.includes(q)) return 25;

  return 0;
}

export function SearchCommand({ open, onOpenChange }: SearchCommandProps) {
  const router = useRouter();
  const [index, setIndex] = useState<SearchEntry[]>([]);
  const [query, setQuery] = useState("");
  const indexLoaded = useRef(false);

  // Load the search index once (on first open or mount)
  useEffect(() => {
    if (indexLoaded.current) return;
    indexLoaded.current = true;

    fetch("/search-index.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load search index");
        return res.json();
      })
      .then((data: SearchEntry[]) => setIndex(data))
      .catch((err) => {
        console.error("[SearchCommand] Could not load search index:", err);
      });
  }, []);

  // Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  // Compute filtered + scored results
  const results = useMemo(() => {
    if (!query || query.length < 1) return [];

    const scored = index
      .map((entry) => ({ entry, score: scoreEntry(entry, query) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30);

    return scored.map((r) => r.entry);
  }, [query, index]);

  // Group results by subspecialty
  const grouped = useMemo(() => {
    const acc: Record<string, SearchEntry[]> = {};
    for (const r of results) {
      if (!acc[r.subspecialty]) acc[r.subspecialty] = [];
      acc[r.subspecialty].push(r);
    }
    return acc;
  }, [results]);

  const handleSelect = useCallback(
    (entry: SearchEntry) => {
      onOpenChange(false);
      setQuery("");
      router.push(`/${entry.subspecialty}/${entry.slug}`);
    },
    [onOpenChange, router]
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      onOpenChange(newOpen);
      if (!newOpen) setQuery("");
    },
    [onOpenChange]
  );

  return (
    <CommandDialog open={open} onOpenChange={handleOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="질환명, 영문명, 약어로 검색..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {query.length > 0
            ? "검색 결과가 없습니다."
            : "질환명을 입력하세요."}
        </CommandEmpty>
        {Object.entries(grouped).map(([subspecialty, items]) => {
          const sub = SUBSPECIALTIES.find((s) => s.id === subspecialty);
          return (
            <CommandGroup
              key={subspecialty}
              heading={sub?.name || subspecialty}
            >
              {items.map((item) => (
                <CommandItem
                  key={`${item.subspecialty}/${item.slug}`}
                  value={`${item.title} ${item.titleEn} ${item.aliases.join(" ")}`}
                  onSelect={() => handleSelect(item)}
                  className="cursor-pointer"
                >
                  <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {item.category}
                      {item.titleEn && ` · ${item.titleEn}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    {item.emergency && (
                      <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                    )}
                    {item.examImportance === "high-yield" && (
                      <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}
